import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import cn from 'classnames'
import { find, get, map, findIndex, pull, values, isEmpty } from 'lodash'
import { Switch, alpha, styled } from '@material-ui/core'

import SidebarModal from '../SidebarModal'
import BackChevron from '../BackChevron'
import Button from '../Button'

import { ReactComponent as Unchecked } from '../../assets/icons/fill-unchecked.svg'
import { ReactComponent as Checkmark } from '../../assets/icons/fill-checkmark.svg'

import styles from './RequestRefundModal.module.scss'

import { stringFloatToCents } from '../../utils/formatters'


const centsToDollars = (cents) => `$${(cents / 100).toFixed(2)}`

const refundReasons = {
  '1': { id: '1', reason: 'Incorrectly entered order' },
  '2': { id: '2', reason: 'Insufficient inventory' },
  '3': { id: '3', reason: 'Item returned, quality related' },
  '4': { id: '4', reason: 'Guest changed their mind' },
  '5': { id: '5', reason: 'Long wait for item' },
}

const RequestRefundModal = ({ refund, order, edit, onComplete, onClose, className, ...props }) => {
  const classNames = cn(styles.requestRefundModal, className)
  const items = get(order, 'itemModels', [])
  const totalInCents = order?.amountInCents ?? 0
  const itemIdIndexes = items.map((_, index) => index)
  const [selectedItems, setSelectedItems] = useState(itemIdIndexes)
  const [mouseDown, setMouseDown] = useState(false)
  const [mouseMoving, setMouseMoving] = useState(false)
  const [refundAmount, setRefundAmount] = useState(0)
  const [refundReason, setRefundReason] = useState(undefined)
  const [editMode, setEditMode] = useState(edit)
  const isEntireOrder = itemIdIndexes.length === selectedItems.length

  useEffect(() => {
    const sumSelectedItems = (accumulator, itemIndex) => accumulator + stringFloatToCents(items[itemIndex]?.price?.replace('$', ''))
    let amountToRefundInCents = selectedItems.reduce(sumSelectedItems, 0)
    if (isEntireOrder || amountToRefundInCents > totalInCents) {
      amountToRefundInCents = totalInCents
    }
    setRefundAmount(amountToRefundInCents)
  }, [selectedItems, items])

  useEffect(() => {
    const refundableItems = items.reduce((accumulator, item, index) => {
      return item.refund ? [...accumulator, index] : [...accumulator]
    }, [])

    setSelectedItems(refundableItems)

    if (refund && refund.reason) {
      const reason = find(values(refundReasons), { reason: refund.reason })
      setRefundReason(reason)
    }
  }, [items])

  const toggleItem = (index) => {
    if (!editMode) return
    const isChecked = selectedItems.includes(index)
    if (isChecked) {
      selectedItems.splice(findIndex(selectedItems, (item) => item === index), 1)
      setSelectedItems([ ...selectedItems ])
    } else {
      setSelectedItems([index, ...selectedItems])
    }
  }

  const uncheckItem = (index) => {
    setSelectedItems([...pull(selectedItems, index)])
  }

  const onSelectEntireOrder = () => {
    if (!editMode) return
    if (isEntireOrder) {
      setSelectedItems([])
    } else {
      setSelectedItems([...itemIdIndexes])
    }
  }

  const refundReasonChanged = (refundReasonId) => {
    if (!editMode) return
    const refundReason = refundReasons[refundReasonId]
    setRefundReason(refundReason)
  }

  const handleOnComplete = () => {
    const orderId = order.uuid
    const itemIndexes = selectedItems
    const itemIds = map(selectedItems, (itemIndex) => items[itemIndex].id)
    const lineItemIds = map(selectedItems, (itemIndex) => items[itemIndex].lineItemUuid)
    const reason = refundReason.reason

    onComplete({ orderId, itemIndexes, itemIds, lineItemIds, reason, amountInCents: refundAmount })
  }

  const renderItems = (item, index) => {
    const isChecked = selectedItems.includes(index)
    let checkmarkElement = isChecked ? <Checkmark /> : <Unchecked />

    return (
      <div className={styles.selectableItem} key={`${item.name}-${index}`} onClick={() => toggleItem(index)}>
        <div className={cn([styles.item, isChecked && styles.selected])}>
          <div className={styles.column}>
            {checkmarkElement}
          </div>
          <div className={styles.column}>
            <span className={styles.name}>{`(1) ${item.name} - ${item.price}`}</span>
          </div>
        </div>
      </div>
    )
  }

  const renderRefundReasons = () => {
    return (
      <select className={styles.refundReason} onChange={(event) => refundReasonChanged(event.target.value)} value={get(refundReason, 'id')}>
        <option value={undefined}>Select a refund reason</option>
        {values(refundReasons).map((refund) => (
          <option value={refund.id}>{refund.reason}</option>
        ))}
      </select>
    )
  }
  
  const BlueSwitch = styled(Switch)(({ theme }) => ({
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: '#2253FF',
      '&:hover': {
        backgroundColor: alpha('#2253FF', theme.palette.action.hoverOpacity),
      },
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#2253FF',
    },
  }));
      
  return (
    <SidebarModal
    show
    title={'Request Refund'}
    onClose={onClose}
    leftAccessory={<BackChevron onClick={onClose} />}
    className={classNames} {...props}
    >

    <span className={styles.border} />

    <div className={styles.requestRefundModal}>
        <div className={styles.orderNumber}>
         {`Order # ${order.orderNumber}`}
        </div>
            {renderRefundReasons()}
          <div className={styles.requiredField}>
               *Required Field
          </div>
            
      <div className={styles.refundContainer}>
      <span className={styles.border} />
      <div className={styles.selectItems}>
        Select Items
      </div>
        <div className={styles.selectableItem} key={"Entire Order"} onClick={onSelectEntireOrder}>
          <div className={cn([styles.item, styles.entireOrder])}>
            <div className={styles.column}>
              <span className={styles.name}>{`Entire Order - ${centsToDollars(totalInCents)}`}</span>
            </div>
            <div className={cn([styles.colomn, styles.entireOrderToggle])}>
              <BlueSwitch  checked={isEntireOrder}/>
            </div>
          </div>
        </div>
        {map(items, renderItems)}
      </div>
      <div className={styles.submitContainer}>
        { editMode && <Button
          className={styles.submitButton}
          disabled = {refundReason === undefined || isEmpty(selectedItems)}
          onClick={() => {
              handleOnComplete()
              onClose()
            }}>
          {`Refund ${centsToDollars(refundAmount)}`}
        </Button> }
      </div>
    </div>
  </SidebarModal>
  )

}

RequestRefundModal.defaultProps = {
  order: {},
	onComplete: () => {},
  onClose: () => {},
  edit: true,
}

RequestRefundModal.propTypes = {
  order: PropTypes.object,
	onComplete: PropTypes.func,
  onClose: PropTypes.func,
  edit: PropTypes.bool,
}

export default RequestRefundModal
